<script setup lang="ts">
import { Tabs, TabPane, Alert } from 'ant-design-vue';

import {
  useFileContentQuery,
  type FileAiReviewFragmentFragment,
  type FileFragmentFragment,
} from '@/graphql/generated/graphql';
import JsonView from '~/ui/JsonView/JsonView.vue';
import JsonParse from '~/ui/JsonView/JsonParse.vue';
import CopyIconButton from '~/ui/CopyIconButton.vue';
import LayoutLeftRight from '~/ui/LayoutLeftRight.vue';
import PreviewFile from '~/ui/PreviewFile/index.vue';
import LoadingError from '~/ui/LoadingError.vue';

const props = defineProps<{
  file: FileFragmentFragment;
  fileAiReview: FileAiReviewFragmentFragment;
}>();

const {
  data: fileContentData,
  fetching: fileContentFetching,
  error: fileContentError,
} = useFileContentQuery({
  variables: computed(() => ({ id: props.fileAiReview.file_content_id as number })),
  pause: computed(() => !props.fileAiReview.file_content_id),
});
const isFileError = ref(false);
const env = useEnv();
</script>

<template>
  <div class="flex min-h-0 flex-1 flex-col">
    <LayoutLeftRight
      v-if="fileAiReview"
      :hide-right-side="!fileAiReview?.parsed_response"
    >
      <template #alerts>
        <Alert
          v-if="isFileError"
          type="error"
          show-icon
          message="Document preview failed"
          description="The file could not be loaded for preview. Only the formatted response is shown below if available."
        />
        <Alert
          v-if="!fileAiReview.parsed_response"
          type="info"
          show-icon
          message="No formatted response available"
          description="The AI review did not return a formatted response."
        />
      </template>
      <template #left>
        <div class="file-ai-review-modal-tabs flex min-h-0 flex-1 flex-col">
          <Tabs>
            <TabPane key="document" tab="Document">
              <div class="min-h-0 flex-1 overflow-auto h-full flex flex-col">
                <PreviewFile
                  v-model:is-error="isFileError"
                  :url="`${env.domains.api}/v2/file/${file.id}`"
                  :ext="file.ext"
                />
              </div>
            </TabPane>
            <TabPane key="file-content" tab="File content">
              <div class="h-full overflow-auto">
                <LoadingError v-if="!fileContentData?.fileContent?.content" :fetching="fileContentFetching" :error="fileContentError" />
                <FileContentView v-else :file-content="fileContentData.fileContent" />
              </div>
            </TabPane>
          </Tabs>
        </div>
      </template>
      <template v-if="fileAiReview?.parsed_response" #right>
        <div class="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div class="flex shrink-0 items-center justify-between">
            <h3 class="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">
              Formatted response
            </h3>
            <CopyIconButton :value="fileAiReview.parsed_response" />
          </div>
          <div class="min-h-0 flex-1 overflow-auto">
            <JsonParse :json="fileAiReview.parsed_response">
              <template #default="{ json }">
                <JsonView :data="json" />
              </template>
            </JsonParse>
          </div>
        </div>
      </template>
    </LayoutLeftRight>
  </div>
</template>

<style scoped>
.file-ai-review-modal-tabs :deep(.ant-tabs) {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
}

.file-ai-review-modal-tabs :deep(.ant-tabs-content) {
  height: 100%;
}
</style>
